import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { Field } from '../components/Field.js';
import { AuthLayout } from './AuthLayout.js';

const MIN_PASSWORD_LENGTH = 12;

export function ResetPasswordPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  if (token === null) {
    return (
      <AuthLayout title="Reset link missing">
        <Alert tone="error">
          This page needs the link from your reset email.{' '}
          <Link to="/forgot-password">Request a new one</Link>.
        </Alert>
      </AuthLayout>
    );
  }

  const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiRequest('/auth/reset-password', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'This reset link could not be used.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="Password changed">
        <Alert tone="success">
          Your password is updated and every other session has been signed out.
        </Alert>
        <Link className="btn btn--primary btn--block" to="/login">
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      {error === null ? null : (
        <Alert tone="error">
          {error} <Link to="/forgot-password">Request a new link</Link>.
        </Alert>
      )}

      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        noValidate
      >
        <Field
          label="New password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          hint={`At least ${String(MIN_PASSWORD_LENGTH)} characters.`}
          error={tooShort ? `Use at least ${String(MIN_PASSWORD_LENGTH)} characters.` : undefined}
        />
        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={submitting || tooShort}
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </AuthLayout>
  );
}
